import { ElementRef, ViewContainerRef } from '@angular/core';
import { ContentControlledEditingDirective } from '../editing-directive/content-controlled-editing-directive';
import { IContentControlStudioTimeline } from '../../../components/content-control/studio/content-control-studio/content-control-studio-timeline/i-content-control-studio-timeline';
import { IStudioKeyboardActions } from './i-studio-keyboard-actions';
import { VideoClip } from '../../../components/content-control/studio/content-control-studio/content-control-studio-actions/content-control-studio-actions.component';
import { IContentControlManager } from '../manager';
import { IEvrideoDialogService } from '../../common/i-evrideo-dialog-service';
import { IStudioEventsRef } from './i-studio-events-ref';
import { IContentControlVideoSource } from '../i-content-control-video-source';
import { ETimelineMode } from './e-timeline-mode';

export abstract class IContentControlStudioViewModel {
  static VK = 'SOURCE';
  static SK = 'NONE';

  isFragmentDialogOpen = false;
  timelineMode: ETimelineMode = ETimelineMode.OriginalTime;

  protected _timelineRef: IContentControlStudioTimeline;
  protected _keyboardActions: IStudioKeyboardActions;

  get timelineRef(): IContentControlStudioTimeline {
    return this._timelineRef;
  }

  setTimelineRef(ref: IContentControlStudioTimeline): void {
    this._timelineRef = ref;
  }

  setKeyboardActions(actions: IStudioKeyboardActions): void {
    this._keyboardActions = actions;
  }

  abstract audioOptions: string[];
  abstract subtitleOptions: string[];
  abstract selectedAudioOption: string;
  abstract availableAudioTracks: { [lang: string]: MediaElementAudioSourceNode };

  abstract onTimelineModeChange(mode: string): void;
  abstract onStudioKeyDown(event: KeyboardEvent): void;
  abstract setupVideo(): void;
  abstract setupSubtitles(subtitleOptions: string[]): string[];
  abstract setupAudio(audioOptions: string[]): string[];
  abstract getEndTimeForMarkIn(currentVideoTimeInMs: number): number;
  abstract shouldUseTimestampForMarkIn(): boolean;
  abstract cleanupOldDirectives(
    directives: ContentControlledEditingDirective[],
    selectedSegmentId?: number,
  ): ContentControlledEditingDirective[];
  abstract supportsPlaybackSpeed(): boolean;
  abstract onVideoLoaded(video: IContentControlVideoSource): void;
  abstract getCurrentTimeForUIAsTimestampMs(): number;
  abstract moveToLiveEdge(): void;
  abstract onWindowResize(): void;

  abstract play(): void;
  abstract pause(): void;
  abstract save(): void;
  abstract changePlaybackSpeed(change: number): void;
  abstract frameUp(multiply?: number): void;
  abstract frameDown(multiply?: number): void;
  abstract timelineZoom(change: number): void;
  abstract timelineZoomFitVideo(): void;
  abstract timelineZoomFitSegment(goTo?: 'in' | 'out'): void;
  abstract goToIn(): void;
  abstract goToOut(): void;
  abstract changeSelectedSegment(id: number, isMulti?: boolean): void;
  abstract chooseWhatToDelete(): void;
  abstract toggleMute(force?: boolean): void;
  abstract toggleMuteKey(): void;
  abstract markIn(): Promise<void>;
  abstract markOut(): Promise<void>;
  abstract calculateZoomLevels(time: number): void;
  abstract getActiveSegment(): any;
  abstract onVideoClipAdd(clip: VideoClip): void;
  abstract get isVideoTimelineFit(): boolean;
  abstract get selectedSegmentId(): number | undefined;

  abstract setupViewModel(
    manager: IContentControlManager,
    dialogService: IEvrideoDialogService,
    viewContainerRef: ViewContainerRef,
    timelineRef: IContentControlStudioTimeline,
    eventsRef: IStudioEventsRef,
    playerElement: HTMLDivElement,
    loadingText: HTMLElement,
    videoSource: IContentControlVideoSource,
    canvasElementRef: ElementRef<HTMLCanvasElement>,
    drawingCanvasElementRef: ElementRef<HTMLCanvasElement>,
    detectChangesFn: () => void,
  ): void;
}
